import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import Animated from 'react-native-reanimated';
import { Platform, View } from 'react-native';
import { AppView } from 'design-system/AppView';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import useColors from 'hooks/useColors';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import { selectHasSubcategories, selectParentCategories, selectNormalizedCategories, selectParentCategoriesWithSubcategories, } from 'reducers/selectors';
import Section from 'design-system/Section';
import { Spacer, TextButtonLink, rem } from 'design-system';
import createStyleSheets from 'utils/createStyleSheets';
import useStyles from 'hooks/useStyles';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import IconCircle from 'design-system/icons/IconCircle';
import Text from 'design-system/Text';
import RadioButton from 'design-system/RadioButton';
import TouchableHighlight from 'utils/packages/TouchableHighlight';
import useRequiredTierCallback from 'features/premium/hooks/useRequiredTierCallback';
import { Tier } from 'features/premium/entities';
import { fetchAnalyticsBudgetingData, updateCategoriesPreferences, } from 'actions';
import Modal from 'utils/packages/Modal';
import { selectBudgets } from 'features/budgeting/selectors';
import { useSaveBudget } from 'features/budgeting/hooks/useSaveBudget';
import { selectIsFetchingTotals } from 'features/analytics/selectors';
import ExcludeSubcategoriesList from '../components/categories/ExcludeSubcategoriesList';
import { isWeb } from '../../../constants';
import IconTransactionCategory from '../icons/IconTransactionCategory';
const keyExtractor = (item) => item.id;
const ExcludeCategoriesScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    const { title, subtitle, budgets: budgetsProp, onSaveLocal, skipSaveBudget, fromAnalytics, fromBudgeting, } = route.params;
    const waitsForRefresh = !!(fromAnalytics || fromBudgeting);
    const budgets = useAppSelector(selectBudgets);
    const budgetsToUse = budgetsProp || budgets;
    const colors = useColors();
    const styles = useStyles(styleSet);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const hasSubcategories = useAppSelector(selectHasSubcategories);
    const parentCategories = useAppSelector(selectParentCategories);
    const normalizedCategories = useAppSelector(selectNormalizedCategories);
    const store = useAppStore();
    const dispatch = useAppDispatch();
    const initialStateRef = useRef({});
    const [excludedCategories, setExcludedCategories] = useState(() => {
        const initialState = parentCategories.reduce((acc, category) => {
            acc[category.id] = !!category.hideFromStats;
            return acc;
        }, {});
        initialStateRef.current = initialState;
        return initialState;
    });
    const hasMadeChanges = useMemo(() => Object.entries(excludedCategories).some(([categoryId, isExcluded]) => isExcluded !== initialStateRef.current[categoryId]), [excludedCategories]);
    const excludedHasBudget = useMemo(() => budgetsToUse.some((budget) => budget.type === 'category' &&
        (!!budget.baseLimit || !!budget.totalLimit) &&
        budget.categoryId &&
        excludedCategories[budget.categoryId]), [budgetsToUse, excludedCategories]);
    const description = useMemo(() => {
        if (subtitle)
            return subtitle;
        const str = 'Select the categories to exclude from your spending in Budgeting and Analytics';
        if (hasSubcategories) {
            return `${str}. If you exclude a parent category, all of its subcategories will be excluded as well`;
        }
        return str;
    }, [hasSubcategories, subtitle]);
    const onPressManageCategories = useCallback(() => {
        navigation.navigateDeprecated('ManageCategories');
    }, []);
    const ListHeaderComponent = useMemo(() => (<>
            <ScreenInfoHeader title={title} subTitle={description}/>
            <Section style={styles.manageSection} title="Categories" button={<TextButtonLink text="Manage" onPress={onPressManageCategories}/>}/>
          </>), [title, description, styles.manageSection, onPressManageCategories]);
    const categoriesWithSubcategories = useAppSelector(selectParentCategoriesWithSubcategories);
    const data = useMemo(() => {
        const alwaysExcluded = [];
        if (normalizedCategories.internal) {
            alwaysExcluded.push({
                ...normalizedCategories.internal,
                alwaysExcluded: true,
            });
        }
        if (normalizedCategories.transfer) {
            alwaysExcluded.push({
                ...normalizedCategories.transfer,
                alwaysExcluded: true,
            });
        }
        const selectable = categoriesWithSubcategories.filter((category) => category.id !== 'internal' && category.id !== 'transfer');
        return [
            ...alwaysExcluded,
            ...selectable.filter((category) => initialStateRef.current[category.id]),
            ...selectable.filter((category) => !initialStateRef.current[category.id]),
        ];
    }, [
        categoriesWithSubcategories,
        normalizedCategories.internal,
        normalizedCategories.transfer,
    ]);
    const renderItem = useCallback(({ item, index }) => (<View style={[
            styles.eachItem,
            (hasSubcategories || index === 0) && styles.startOfCard,
            (hasSubcategories || index === data.length - 1) &&
                styles.endOfCard,
            hasSubcategories && styles.marginBottom,
        ]}>
            {item.alwaysExcluded ? (<View style={styles.parentCategory}>
                <IconCircle width={44} height={44} style={styles.opacity50}>
                  <IconTransactionCategory category={item}/>
                </IconCircle>
                <Spacer.W16 />
                <View>
                  <Text Text-16 style={styles.opacity50}>
                    {item.displayName}
                  </Text>
                  <Text TextThin-14 Secondary>
                    Always excluded
                  </Text>
                </View>
                <Spacer flex/>
                <RadioButton type="multiple" isChecked disabled/>
              </View>) : (<TouchableHighlight onCard hoverStyle={{}} activeOpacity={1} underlayColor={colors.background.underlay} onPress={() => setExcludedCategories((prev) => ({
                ...prev,
                [item.id]: !prev[item.id],
            }))}>
                <View>
                  <View style={styles.parentCategory}>
                    <IconCircle width={44} height={44}>
                      <IconTransactionCategory category={item}/>
                    </IconCircle>
                    <Spacer.W16 />
                    <Text Text-16>{item.displayName}</Text>
                    <Spacer flex/>
                    <RadioButton type="multiple" isChecked={excludedCategories[item.id]}/>
                  </View>
                  {!!item.subcategories?.length && (<ExcludeSubcategoriesList subcategories={item.subcategories}/>)}
                </View>
              </TouchableHighlight>)}
          </View>), [
        colors.background.underlay,
        data.length,
        excludedCategories,
        hasSubcategories,
        styles.eachItem,
        styles.endOfCard,
        styles.marginBottom,
        styles.opacity50,
        styles.parentCategory,
        styles.startOfCard,
    ]);
    const [, saveBudget] = useSaveBudget();
    const isFetchingTotals = useAppSelector(selectIsFetchingTotals);
    const startedFetchingTotals = useRef(false);
    const [startedFetching, setStartedFetching] = useState(false);
    useEffect(() => {
        if (waitsForRefresh && startedFetching && isFetchingTotals) {
            startedFetchingTotals.current = true;
        }
        if (waitsForRefresh &&
            startedFetching &&
            !isFetchingTotals &&
            startedFetchingTotals.current) {
            navigation.pop();
        }
    }, [waitsForRefresh, isFetchingTotals, startedFetching]);
    const updateExcluded = useCallback(async () => {
        if (waitsForRefresh)
            setStartedFetching(true);
        const { categories, customCategories } = store.getState().user;
        const categoryPreferences = Object.entries(excludedCategories).map(([categoryId, isExcluded]) => ({ categoryId, isExcluded }));
        const newMonthlyBudget = budgetsToUse.map((budget) => {
            if (budget.type === 'category' &&
                budget.categoryId &&
                (excludedCategories[budget.categoryId] ||
                    excludedCategories[normalizedCategories[budget.categoryId]?.parentCategoryId ?? ''])) {
                return { ...budget, totalLimit: null };
            }
            return budget;
        });
        onSaveLocal?.(newMonthlyBudget);
        const prevCategoriesState = { categories, customCategories };
        const shouldRollover = budgetsToUse.some((budget) => !!budget.shouldRollover);
        if (fromBudgeting) {
            await dispatch(updateCategoriesPreferences(categoryPreferences, prevCategoriesState));
            if (!skipSaveBudget) {
                await saveBudget(undefined, shouldRollover, newMonthlyBudget);
            }
            dispatch(fetchAnalyticsBudgetingData(true));
            return;
        }
        if (fromAnalytics) {
            await dispatch(updateCategoriesPreferences(categoryPreferences, prevCategoriesState));
            dispatch(fetchAnalyticsBudgetingData(true));
        }
        else {
            dispatch(updateCategoriesPreferences(categoryPreferences, prevCategoriesState));
        }
        if (!skipSaveBudget) {
            saveBudget(undefined, shouldRollover, newMonthlyBudget);
        }
        if (!fromAnalytics)
            navigation.pop();
    }, [
        budgetsToUse,
        excludedCategories,
        fromAnalytics,
        fromBudgeting,
        waitsForRefresh,
        normalizedCategories,
        onSaveLocal,
        saveBudget,
        skipSaveBudget,
    ]);
    const onSave = useRequiredTierCallback(useCallback(() => {
        if (!hasMadeChanges) {
            navigation.pop();
            return;
        }
        if (excludedHasBudget) {
            Modal.showConfirmation({
                title: 'Are you sure?',
                subtitle: 'These categories have budgets. Saving these changes will exclude them from your budget and reset their values.',
                confirmButtonTitle: 'No, thanks',
                cancelButtonTitle: 'Yes, let me save',
                onPressCancel: updateExcluded,
            });
        }
        else {
            updateExcluded();
        }
    }, [hasMadeChanges, excludedHasBudget, updateExcluded]), Tier.pro);
    return (<AppView type="secondary" withFrame={false}>
          <Animated.FlatList onScroll={scrollHandler} scrollEventThrottle={16} data={data} renderItem={renderItem} ListHeaderComponent={ListHeaderComponent} contentContainerStyle={contentContainerStyle} keyExtractor={keyExtractor}/>
          <Button title="Save" brand floating onPress={onSave} isFetching={!!(waitsForRefresh && startedFetching)}/>
        </AppView>);
}, ({ colors, route: { params } }) => ({
    title: 'Exclude categories',
    hideTitleOnScroll: !!params.title,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styleSet = createStyleSheets((colors) => ({
    manageSection: {
        marginBottom: rem(isWeb ? -8 : 0),
    },
    endOfCard: {
        marginHorizontal: rem(16),
        borderBottomLeftRadius: rem(16),
        borderBottomRightRadius: rem(16),
        backgroundColor: colors.cards.onDark,
        ...(Platform.OS === 'ios' && {
            overflow: 'hidden',
        }),
        ...(isWeb && {
            paddingBottom: rem(8),
            paddingHorizontal: rem(8),
        }),
    },
    startOfCard: {
        marginHorizontal: rem(16),
        borderTopLeftRadius: rem(16),
        borderTopRightRadius: rem(16),
        backgroundColor: colors.cards.onDark,
        ...(Platform.OS === 'ios' && {
            overflow: 'hidden',
        }),
        ...(isWeb && {
            paddingTop: rem(8),
            paddingHorizontal: rem(8),
        }),
    },
    eachItem: {
        marginHorizontal: rem(16),
        backgroundColor: colors.cards.onDark,
        ...(isWeb && {
            paddingHorizontal: rem(8),
        }),
    },
    marginBottom: {
        marginBottom: rem(16),
    },
    parentCategory: {
        padding: rem(16),
        flexDirection: 'row',
        alignItems: 'center',
    },
    opacity50: {
        opacity: 0.5,
    },
}));
export default ExcludeCategoriesScreen;
